import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoCircleIcon from '@untitled-ui/icons-react/build/esm/InfoCircle';
import SearchMdIcon from '@untitled-ui/icons-react/build/esm/SearchMd';
import SvgIcon from '@mui/material/SvgIcon';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/paths';
import { MdsPatientRow, MdsSortOption } from 'src/services/documentService/getMdsPatients';
import { buildDueChips } from './due-chips';
import { useMdsPatients, DEFAULT_PAGE_SIZE } from './use-mds-patients';
import { actorLabel } from './worksheet-utils';

/**
 * BRK-1851: the MDS Dashboard patient list (contract v1.1 §5.1 / UX spec).
 * All due math is server-computed on the facility's day; this component only
 * renders. Stat tiles with per-category counts are deferred (the list API has
 * no per-category count support) — needs-attention is a single toggle.
 */

// BRK-2163: the GG column is a count of documented items, not a score — the bare "GG 14/26" was
// being read as the PDPM function score.
const GG_DOCUMENTED_TOOLTIP =
  'Section GG self-care and mobility items with a documented score in the last 90 days or on ' +
  'the next assessment. This is a documentation count, not the PDPM function score.';

const formatDate = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const worksheetStatusLabel = (row: MdsPatientRow): string => {
  const status = row.worksheetStatus;
  if (!status || status.state === 'NOT_STARTED') {
    return 'Not started';
  }
  const actor =
    status.lastActorName || status.lastActorRole
      ? ` · ${actorLabel(status.lastActorName, status.lastActorRole)}`
      : '';
  const when = status.lastAt
    ? ` · ${new Date(status.lastAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })}`
    : '';
  return `Submitted v${status.version}${actor}${when}`;
};

const MdsPatientList: React.FC = () => {
  const navigate = useNavigate();
  const facilityUuid = localStorage.getItem('facilityId') || '';
  const { filters, updateFilter, data, loading, error } = useMdsPatients(facilityUuid);

  const rows = data?.content ?? [];

  return (
    <Stack
      gap="1.5rem"
      sx={{ width: '100%', padding: '2rem 2rem 2rem 4rem' }}
    >
      <Typography variant="h4">MDS Dashboard</Typography>

      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap="1rem"
      >
        <OutlinedInput
          placeholder="Search patient name"
          value={filters.search}
          onChange={(e) => updateFilter({ search: e.target.value })}
          startAdornment={
            <SvgIcon
              fontSize="small"
              sx={{ mr: 1 }}
            >
              <SearchMdIcon />
            </SvgIcon>
          }
          size="small"
          sx={{ minWidth: 280 }}
          inputProps={{ 'aria-label': 'Search patient name' }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.active}
              onChange={(e) => updateFilter({ active: e.target.checked })}
            />
          }
          label="Active"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.inactive}
              onChange={(e) => updateFilter({ inactive: e.target.checked })}
            />
          }
          label="Inactive"
        />
        <FormControlLabel
          control={
            <Switch
              checked={filters.needsAttentionOnly}
              onChange={(e) => updateFilter({ needsAttentionOnly: e.target.checked })}
            />
          }
          label="Needs attention only"
        />
        <Select
          value={filters.sort}
          onChange={(e) => updateFilter({ sort: e.target.value as MdsSortOption })}
          size="small"
          inputProps={{ 'aria-label': 'Sort patients' }}
        >
          <MenuItem value="name_asc">Name A–Z</MenuItem>
          <MenuItem value="name_desc">Name Z–A</MenuItem>
          <MenuItem value="deadline_asc">Deadline (soonest)</MenuItem>
        </Select>
      </Stack>

      {error && <Typography color="error">{error}</Typography>}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography
          color="text.secondary"
          sx={{ py: 4 }}
        >
          {filters.search || filters.needsAttentionOnly
            ? 'No patients match the current filters.'
            : 'No patients found for this facility.'}
        </Typography>
      ) : (
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Admitted</TableCell>
              <TableCell>Next assessment</TableCell>
              <TableCell>Due status</TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                >
                  GG items documented
                  <Tooltip
                    title={GG_DOCUMENTED_TOOLTIP}
                    describeChild
                  >
                    <IconButton
                      size="small"
                      aria-label="About GG items documented"
                    >
                      <SvgIcon
                        fontSize="small"
                        color="action"
                      >
                        <InfoCircleIcon />
                      </SvgIcon>
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
              <TableCell>Worksheet</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.patientUuid}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`${paths.mdsDashboard}/${row.patientUuid}`)}
              >
                <TableCell>
                  <Typography variant="subtitle2">{row.patientName}</Typography>
                  {row.mrn && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      MRN {row.mrn}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.active ? 'Active' : 'Inactive'}
                    color={row.active ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{formatDate(row.admittedDate)}</TableCell>
                <TableCell>
                  {row.nextAssessment ? (
                    <>
                      <Typography variant="body2">
                        {row.nextAssessment.assessmentType || 'Assessment'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        ARD {formatDate(row.nextAssessment.ard)}
                      </Typography>
                    </>
                  ) : (
                    'None scheduled'
                  )}
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={0.5}
                  >
                    {buildDueChips(row).map((chip) => (
                      <Chip
                        key={chip.key}
                        size="small"
                        label={chip.label}
                        color={chip.color}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  {row.ggCompleteness
                    ? `${row.ggCompleteness.scored} of ${row.ggCompleteness.total}`
                    : '—'}
                </TableCell>
                <TableCell>{worksheetStatusLabel(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {data && data.totalElements > 0 && (
        <TablePagination
          component="div"
          count={data.totalElements}
          page={filters.page}
          onPageChange={(_e, page) => updateFilter({ page })}
          rowsPerPage={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
        />
      )}
    </Stack>
  );
};

export default MdsPatientList;
